import React from 'react'
import Modal from './Modal'
import { toast } from '../toast/CustomToast'

const copyIdToClipboard = (id) => {
    navigator.clipboard.writeText(id).then(() => {
        toast.success('ID copiado para área de transferência')
    }).catch(() => {
        toast.error('Erro ao copiar ID')
    })
}

const OVERALL_CFG = {
    success: { label: 'Operando normalmente', dot: 'bg-green-500', text: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' },
    warning: { label: 'Atenção: nem todos os canais estão ativos', dot: 'bg-yellow-500', text: 'text-yellow-700 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' },
    error:   { label: 'Falha: nenhum canal ativo', dot: 'bg-red-500', text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' },
}

const ServiceRow = ({ service }) => (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
        <div className="flex items-start gap-2.5 min-w-0">
            <span
                className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${service.healthy ? 'bg-green-500' : 'bg-red-500'}`}
                title={service.healthy ? 'Ativo' : 'Inativo'}
            />
            <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" title={service.name}>
                    {service.name || 'Sem nome'}
                </p>
                <p className="flex items-center gap-1 text-[11px] font-mono text-gray-400 dark:text-gray-500 min-w-0">
                    <span className="truncate" title={service.id}>{service.id || '—'}</span>
                    {service.id && (
                        <button
                            type="button"
                            onClick={() => copyIdToClipboard(service.id)}
                            title="Copiar ID"
                            className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                        >
                            <span className="material-icons leading-none" style={{ fontSize: 12 }}>content_copy</span>
                        </button>
                    )}
                </p>
            </div>
        </div>
        <span
            className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                service.healthy
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            }`}
        >
            {service.state || '—'}
        </span>
    </div>
)

const ServiceRowSkeleton = () => (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 animate-pulse">
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <span className="w-2 h-2 rounded-full shrink-0 mt-1.5 bg-gray-200 dark:bg-gray-700" />
            <div className="min-w-0 flex-1 space-y-1.5">
                <span className="block h-3 w-2/5 rounded bg-gray-200 dark:bg-gray-700" />
                <span className="block h-2.5 w-3/5 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
        </div>
        <span className="shrink-0 h-4 w-16 rounded-full bg-gray-200 dark:bg-gray-700" />
    </div>
)

const MiddlewareStatusModal = ({ isOpen, onClose, status, services = [], error, onRefresh, refreshing }) => {
    const overall = OVERALL_CFG[status] || OVERALL_CFG.error

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Status do Middleware">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border mb-4 ${overall.bg}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${overall.dot}`} />
                <span className={`text-sm font-medium ${overall.text}`}>{overall.label}</span>
                {onRefresh && (
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={refreshing}
                        title="Atualizar status"
                        className="ml-auto shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-lg text-current opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 transition disabled:opacity-40"
                    >
                        <span className={`material-icons leading-none ${refreshing ? 'animate-spin' : ''}`} style={{ fontSize: 15 }}>refresh</span>
                    </button>
                )}
            </div>

            {error && !refreshing && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{error}</p>
            )}

            {refreshing ? (
                <div className="space-y-2">
                    {Array.from({ length: services.length || 3 }).map((_, i) => (
                        <ServiceRowSkeleton key={i} />
                    ))}
                </div>
            ) : services.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum canal retornado pelo middleware.</p>
            ) : (
                <div className="space-y-2">
                    {services.map((service) => (
                        <ServiceRow key={service.id || service.name} service={service} />
                    ))}
                </div>
            )}
        </Modal>
    )
}

export default MiddlewareStatusModal
